<template>
  <section class="ppt">
    <header class="ppt__header">
      <div>
        <h1 class="ppt__title">{{ $t('pad_presence_testing') }}</h1>
        <p class="ppt__subtitle">{{ $t('pad_presence_testing_subtitle') }}</p>
      </div>
      <div class="ppt__header-meta">
        <span v-if="lastUpdated" class="ppt__updated">
          {{ $t('pad_presence_last_updated') }}: {{ lastUpdatedLabel }}
        </span>
      </div>
    </header>

    <div class="ppt__filters">
      <div class="ppt__field">
        <span>{{ $t('pad_presence_window') }}</span>
        <div class="ppt__windows">
          <button
            v-for="option in timeWindowOptions"
            :key="option.id"
            type="button"
            class="ppt__window"
            :class="{ 'ppt__window--on': timeWindow === option.id }"
            @click="timeWindow = option.id"
          >
            {{ $t(option.label) }}
          </button>
        </div>
      </div>
      <label class="ppt__field">
        <span>{{ $t('pad_presence_phone') }}</span>
        <select v-model="phoneNumber" :disabled="loading">
          <option value="">{{ $t('pad_presence_all_phones') }}</option>
          <option v-for="phone in phoneOptions" :key="phone" :value="phone">
            {{ phone }}
          </option>
        </select>
      </label>
      <label class="ppt__field">
        <span>{{ $t('pad_presence_employee') }}</span>
        <select v-model="employeeId" :disabled="loading">
          <option value="">{{ $t('pad_presence_all_employees') }}</option>
          <option
            v-for="employee in employeeOptions"
            :key="employee.id"
            :value="String(employee.id)"
          >
            {{ employee.name }}
          </option>
        </select>
      </label>
      <label class="ppt__field">
        <span>{{ $t('pad_presence_room') }}</span>
        <select v-model="roomId" :disabled="loading">
          <option value="">{{ $t('pad_presence_all_rooms') }}</option>
          <option
            v-for="room in roomOptions"
            :key="room.id"
            :value="String(room.id)"
          >
            {{ room.name }}
          </option>
        </select>
      </label>
    </div>

    <div v-if="!siteId" class="ppt__empty">
      <p class="ppt__empty-title">{{ $t('please_select_company_site') }}</p>
      <p class="ppt__empty-desc">{{ $t('pad_presence_select_site_hint') }}</p>
    </div>

    <p v-else-if="errorMessage" class="ppt__error">{{ errorMessage }}</p>

    <template v-else>
      <section v-if="openSessions.length" class="ppt__card ppt__now">
        <h2 class="ppt__now-title">
          {{ $t('pad_presence_in_room_now') }} ({{ openSessions.length }})
        </h2>
        <ul class="ppt__now-list">
          <li v-for="session in openSessions" :key="session.presenceSessionId" class="ppt__now-item">
            <div class="ppt__now-main">
              <strong>{{ sessionPerson(session) }}</strong>
              {{ $t('pad_presence_in_room') }}
              <strong>{{ session.roomName || '—' }}</strong>
            </div>
            <div class="ppt__meta">
              <span>{{ $t('pad_presence_since') }} {{ formatTime(session.enterAt) }} ({{ liveDuration(session.enterAt) }})</span>
              <span v-if="session.sensorCode">{{ $t('pad_presence_pad') }} {{ session.sensorCode }}</span>
              <span v-if="deviceLabel(session)">{{ deviceLabel(session) }}</span>
              <span
                v-if="session.enterRssi != null"
                class="ppt__rssi"
                :class="rssiClass(session.enterRssi)"
              >
                {{ $t('pad_presence_rssi') }} {{ session.enterRssi }}
              </span>
            </div>
            <div class="ppt__meta">
              <span>{{ $t('pad_presence_auto_exit_leave') }} {{ formatDuration(autoExitTotalSec) }}</span>
              <span v-if="autoExitRemainingSec(session) > 0">
                {{ $t('pad_presence_auto_exit_earliest') }} {{ formatDuration(autoExitRemainingSec(session)) }}
              </span>
              <span v-else class="ppt__warn">{{ $t('pad_presence_auto_exit_passed') }}</span>
            </div>
          </li>
        </ul>
      </section>

      <section class="ppt__card">
        <button type="button" class="ppt__toggle" @click="toggleSection('pads')">
          <span>{{ $t('pad_presence_pads') }} ({{ padRows.length }})</span>
          <span class="ppt__chevron" :class="{ 'ppt__chevron--open': openSections.pads }">▾</span>
        </button>
        <div v-show="openSections.pads" class="ppt__table-wrap">
          <p v-if="!padRows.length" class="ppt__empty-desc">{{ $t('pad_presence_no_pads') }}</p>
          <table v-else>
            <thead>
              <tr>
                <th>{{ $t('pad_presence_room') }}</th>
                <th>{{ $t('pad_presence_pad') }}</th>
                <th>{{ $t('pad_presence_last_enter') }}</th>
                <th>{{ $t('pad_presence_last_offline') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pad in padRows" :key="pad.key">
                <td>{{ pad.roomName || '—' }}</td>
                <td>{{ pad.sensorCode || pad.deviceCode || '—' }}</td>
                <td>{{ formatTime(pad.lastEnterAt) }}</td>
                <td>{{ formatTime(pad.lastOfflineAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="ppt__card">
        <button type="button" class="ppt__toggle" @click="toggleSection('timeline')">
          <span>{{ $t('pad_presence_timeline') }} ({{ timelineRows.length }})</span>
          <span class="ppt__chevron" :class="{ 'ppt__chevron--open': openSections.timeline }">▾</span>
        </button>
        <div v-show="openSections.timeline">
          <p v-if="!timelineRows.length" class="ppt__empty-desc">{{ $t('pad_presence_no_events') }}</p>
          <ul v-else class="ppt__timeline">
            <li
              v-for="row in timelineRows"
              :key="row.key"
              class="ppt__row"
              :class="{ 'ppt__row--fresh': isRecent(row.time) }"
            >
              <span class="ppt__badge" :class="`ppt__badge--${row.kind}`">{{ row.badge }}</span>
              <div class="ppt__row-main">
                <strong>{{ row.title }}</strong>
                <span class="ppt__servik" v-if="row.servik">
                  <span class="ppt__servik-icon" :style="{ backgroundColor: row.servik.color }">
                    <img v-if="row.servik.icon" :src="row.servik.icon" alt="" />
                  </span>
                  {{ $t(row.servik.name) }}
                  <em v-if="row.count > 1">×{{ row.count }}</em>
                </span>
                <div class="ppt__meta">
                  <span v-if="row.phone">{{ row.phone }}</span>
                  <span v-if="row.device">{{ row.device }}</span>
                  <span v-if="row.pad">{{ $t('pad_presence_pad') }} {{ row.pad }}</span>
                  <span
                    v-if="row.rssi != null"
                    class="ppt__rssi"
                    :class="rssiClass(row.rssi)"
                  >
                    {{ $t('pad_presence_rssi') }} {{ row.rssi }}
                  </span>
                </div>
              </div>
              <div class="ppt__row-time">
                <span v-if="isRecent(row.time)" class="ppt__badge ppt__badge--fresh">
                  {{ $t('pad_presence_just_now') }}
                </span>
                <time>{{ row.timeLabel }}</time>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section class="ppt__card">
        <button type="button" class="ppt__toggle" @click="toggleSection('sessions')">
          <span>{{ $t('pad_presence_sessions') }} ({{ filteredSessions.length }})</span>
          <span class="ppt__chevron" :class="{ 'ppt__chevron--open': openSections.sessions }">▾</span>
        </button>
        <div v-show="openSections.sessions" class="ppt__table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ $t('pad_presence_employee') }}</th>
                <th>{{ $t('pad_presence_room') }}</th>
                <th>{{ $t('pad_presence_enter') }}</th>
                <th>{{ $t('pad_presence_exit') }}</th>
                <th>{{ $t('pad_presence_duration') }}</th>
                <th>{{ $t('pad_presence_exit_type') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filteredSessions.length">
                <td colspan="6">{{ $t('pad_presence_no_events') }}</td>
              </tr>
              <tr
                v-for="session in filteredSessions"
                :key="session.presenceSessionId"
                :class="{ 'ppt__fresh': isRecent(sessionTime(session)) }"
              >
                <td>
                  <div>{{ session.employeeName || session.phoneNumber || '—' }}</div>
                  <div class="ppt__meta">
                    <span v-if="session.employeeName && session.phoneNumber">{{ session.phoneNumber }}</span>
                    <span v-if="deviceLabel(session)">{{ deviceLabel(session) }}</span>
                  </div>
                </td>
                <td>
                  <div>{{ session.roomName || '—' }}</div>
                  <div v-if="session.sensorCode" class="ppt__meta">
                    {{ $t('pad_presence_pad') }} {{ session.sensorCode }}
                  </div>
                </td>
                <td>
                  <div>{{ formatTime(session.enterAt) }}</div>
                  <span
                    v-if="session.enterRssi != null"
                    class="ppt__rssi"
                    :class="rssiClass(session.enterRssi)"
                  >
                    {{ $t('pad_presence_rssi') }} {{ session.enterRssi }}
                  </span>
                </td>
                <td>
                  <span v-if="session.isOpen" class="ppt__badge ppt__badge--open">{{ $t('pad_presence_open') }}</span>
                  <span v-else>{{ formatTime(session.exitAt) }}</span>
                </td>
                <td>{{ session.isOpen ? liveDuration(session.enterAt) : formatDuration(session.durationSec) }}</td>
                <td>{{ exitTypeLabel(session) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="ppt__card">
        <button type="button" class="ppt__toggle" @click="toggleSection('offline')">
          <span>{{ $t('pad_presence_offline_presses') }} ({{ filteredOfflineRelays.length }})</span>
          <span class="ppt__chevron" :class="{ 'ppt__chevron--open': openSections.offline }">▾</span>
        </button>
        <div v-show="openSections.offline" class="ppt__table-wrap">
          <table>
            <thead>
              <tr>
                <th>{{ $t('pad_presence_servik') }}</th>
                <th>{{ $t('pad_presence_button') }}</th>
                <th>{{ $t('pad_presence_room') }}</th>
                <th>{{ $t('pad_presence_employee') }}</th>
                <th>{{ $t('pad_presence_count') }}</th>
                <th>{{ $t('pad_presence_time_from') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!filteredOfflineRelays.length">
                <td colspan="6">{{ $t('pad_presence_no_events') }}</td>
              </tr>
              <tr
                v-for="press in filteredOfflineRelays"
                :key="offlineKey(press)"
                :class="{
                  'ppt__faulty': press.faulty,
                  'ppt__fresh': !press.faulty && isRecent(press.timeMs),
                }"
              >
                <td>
                  <span class="ppt__servik" v-if="press.servik">
                    <span class="ppt__servik-icon" :style="{ backgroundColor: press.servik.color }">
                      <img v-if="press.servik.icon" :src="press.servik.icon" alt="" />
                    </span>
                    {{ $t(press.servik.name) }}
                  </span>
                  <span v-else>—</span>
                </td>
                <td>{{ press.btn != null ? press.btn : '—' }}</td>
                <td>
                  <div>{{ press.roomName || '—' }}</div>
                  <div v-if="press.sensorCode || press.deviceCode" class="ppt__meta">
                    {{ $t('pad_presence_pad') }} {{ press.sensorCode || press.deviceCode }}
                  </div>
                </td>
                <td>
                  <div>{{ press.phone || '—' }}</div>
                  <div v-if="deviceLabel(press)" class="ppt__meta">{{ deviceLabel(press) }}</div>
                </td>
                <td>{{ press.count != null ? press.count : '—' }}</td>
                <td>
                  <span v-if="isRecent(press.timeMs)" class="ppt__badge ppt__badge--fresh">
                    {{ $t('pad_presence_just_now') }}
                  </span>
                  {{ formatTime(press.time) }}
                  <span v-if="press.faulty" class="ppt__badge ppt__badge--faulty">{{ $t('pad_presence_faulty') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { getPadPresenceTesting } from '../services/padPresenceTestingService'
import utilService from '../../common/services/utilService'
import { servikMap } from '@/modules/surveys/consts/consts'

const SERVIK_CHIP_COLORS = {
  cleaned: '#5f9b46',
  fixed: '#5f9b46',
  officials: '#5f9b46',
  reports: '#fe5060',
  problem: '#fe5060',
}

function servikChipColor(ctg, id) {
  if (id === 216 || id === 1008) return '#AE7EA3'
  return SERVIK_CHIP_COLORS[ctg] || '#fe5060'
}

function servikIcon(ctg, id, name) {
  try {
    return require(`@/assets/survey/servik_icons_white/${ctg}/${id}.${name}.svg`)
  } catch (err) {
    return null
  }
}

const SERVIK_LOOKUP = Object.entries(servikMap || {}).reduce((map, [ctg, list]) => {
  for (const servik of list || []) {
    map[Number(servik.id)] = {
      ...servik,
      ctg,
      icon: servikIcon(ctg, servik.id, servik.name),
      color: servikChipColor(ctg, servik.id),
    }
  }
  return map
}, {})

const POLL_INTERVAL_MS = 15000
const NOW_TICK_MS = 1000
const RECENT_MS = 30000
const ENTER_RSSI_MIN = -78
const EXIT_TIMEOUT_SEC = 300
const EXIT_CONFIRM_SEC = 45
const AUTO_EXIT_TOTAL_SEC = EXIT_TIMEOUT_SEC + EXIT_CONFIRM_SEC
const HOUR_MS = 60 * 60 * 1000

function startOfCompanyDayMs(nowMs, gmt) {
  const dateStr = utilService.companyLocalDateStr(nowMs, gmt)
  if (!dateStr || gmt == null || Number.isNaN(Number(gmt))) {
    const start = new Date(nowMs)
    start.setHours(0, 0, 0, 0)
    return start.getTime()
  }
  const [year, month, day] = dateStr.split('-').map(Number)
  return Date.UTC(year, month - 1, day) - Number(gmt) * HOUR_MS
}

function lastMonthRange() {
  const timeTo = new Date()
  const timeFrom = new Date(timeTo)
  timeFrom.setMonth(timeFrom.getMonth() - 1)
  return {
    timeFrom: timeFrom.toISOString(),
    timeTo: timeTo.toISOString(),
  }
}

export default {
  name: 'PadPresenceTesting',
  data() {
    return {
      openSections: {
        pads: true,
        timeline: true,
        sessions: false,
        offline: false,
      },
      timeWindow: 'today',
      timeWindowOptions: [
        { id: 'hour', label: 'pad_presence_window_hour' },
        { id: 'today', label: 'pad_presence_window_today' },
        { id: 'month', label: 'pad_presence_window_month' },
      ],
      autoExitTotalSec: AUTO_EXIT_TOTAL_SEC,
      employeeId: '',
      phoneNumber: '',
      roomId: '',
      events: [],
      sessions: [],
      offlineRelays: [],
      pads: [],
      companyGmt: null,
      loading: false,
      lastUpdated: null,
      errorMessage: null,
      pollTimer: null,
      nowTimer: null,
      nowTick: Date.now(),
      requestId: 0,
    }
  },
  computed: {
    ...mapGetters(['filterSelected', 'filterOptions']),
    siteId() {
      return this.filterSelected && this.filterSelected.site
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return ''
      return this.formatTime(this.lastUpdated)
    },
    windowStartMs() {
      if (this.timeWindow === 'hour') return this.nowTick - HOUR_MS
      if (this.timeWindow === 'month') return 0
      return startOfCompanyDayMs(this.nowTick, this.companyGmt)
    },
    phoneOptions() {
      const seen = new Set()
      for (const event of this.events) {
        if (event.phoneNumber) seen.add(event.phoneNumber)
      }
      for (const press of this.offlineRelays) {
        if (press.phone) seen.add(press.phone)
      }
      return Array.from(seen).sort((a, b) => a.localeCompare(b))
    },
    employeeOptions() {
      const seen = new Map()
      for (const event of this.events) {
        if (event.employeeId == null || seen.has(event.employeeId)) continue
        seen.set(event.employeeId, {
          id: event.employeeId,
          name: event.employeeName || event.phoneNumber || String(event.employeeId),
        })
      }
      return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
    },
    roomOptions() {
      const rooms = (this.filterOptions && this.filterOptions.rooms) || []
      if (rooms.length) {
        return rooms
          .map((room) => ({ id: room.id, name: room.display_name || room.name || String(room.id) }))
          .sort((a, b) => a.name.localeCompare(b.name))
      }
      const seen = new Map()
      for (const event of this.events) {
        if (event.roomId == null || seen.has(event.roomId)) continue
        seen.set(event.roomId, { id: event.roomId, name: event.roomName || String(event.roomId) })
      }
      return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredEvents() {
      return this.events.filter((event) => (
        this.matchesFilters(event) && this.inTimeWindow(event.detectedAt)
      ))
    },
    filteredSessions() {
      const enterRssiBySession = new Map()
      for (const event of this.events) {
        if (event.eventType !== 'enter' || !event.presenceSessionId) continue
        enterRssiBySession.set(event.presenceSessionId, event.rssi ?? null)
      }
      return this.sessions
        .filter((session) => {
          if (!this.matchesFilters(session)) return false
          if (session.isOpen) return true
          return this.inTimeWindow(session.enterAt) || this.inTimeWindow(session.exitAt)
        })
        .map((session) => ({
          ...session,
          enterRssi: enterRssiBySession.get(session.presenceSessionId) ?? null,
        }))
    },
    openSessions() {
      return this.filteredSessions.filter((session) => session.isOpen)
    },
    selectedEmployeePhones() {
      if (!this.employeeId) return null
      const phones = new Set()
      for (const event of this.events) {
        if (String(event.employeeId) !== String(this.employeeId) || !event.phoneNumber) continue
        phones.add(event.phoneNumber)
      }
      return phones
    },
    filteredOfflineRelays() {
      return this.offlineRelays
        .filter((press) => {
          if (this.roomId && String(press.roomId) !== String(this.roomId)) return false
          if (this.phoneNumber && press.phone !== this.phoneNumber) return false
          if (this.selectedEmployeePhones && !this.selectedEmployeePhones.has(press.phone)) return false
          if (!this.inTimeWindow(press.time)) return false
          return true
        })
        .map((press) => ({
          ...press,
          servik: this.servikFor(press.btn),
          timeMs: press.time ? new Date(press.time).getTime() : 0,
        }))
    },
    timelineRows() {
      const rows = []
      for (const event of this.filteredEvents) {
        rows.push({
          key: `ev-${event.eventUuid}`,
          kind: event.eventType === 'enter' ? 'enter' : (event.exitKind || 'phone'),
          badge: this.eventBadge(event),
          title: event.roomName || '—',
          phone: event.phoneNumber || null,
          device: this.deviceLabel(event),
          pad: event.sensorCode || null,
          rssi: event.rssi ?? null,
          time: new Date(event.detectedAt).getTime(),
          timeLabel: this.formatTime(event.detectedAt),
        })
      }
      for (const press of this.filteredOfflineRelays) {
        rows.push({
          key: this.offlineKey(press),
          kind: press.faulty ? 'faulty' : 'offline',
          badge: this.$t('pad_presence_offline'),
          title: press.roomName || '—',
          phone: press.phone || null,
          device: this.deviceLabel(press),
          pad: press.sensorCode || press.deviceCode || null,
          rssi: null,
          servik: press.servik,
          count: press.count,
          time: press.timeMs,
          timeLabel: this.formatTime(press.time),
        })
      }
      return rows.sort((a, b) => b.time - a.time)
    },
    padRows() {
      return this.pads
        .filter((pad) => !this.roomId || String(pad.roomId) === String(this.roomId))
        .map((pad) => {
          const codes = new Set([pad.sensorCode, pad.deviceCode].filter(Boolean))
          let lastEnterAt = null
          for (const event of this.events) {
            if (event.eventType !== 'enter') continue
            if (!this.matchesFilters(event)) continue
            if (codes.size && event.sensorCode && !codes.has(event.sensorCode)) continue
            if (!codes.size && String(event.roomId) !== String(pad.roomId)) continue
            if (!lastEnterAt || new Date(event.detectedAt) > new Date(lastEnterAt)) {
              lastEnterAt = event.detectedAt
            }
          }
          let lastOfflineAt = null
          for (const press of this.offlineRelays) {
            if (this.phoneNumber && press.phone !== this.phoneNumber) continue
            if (this.selectedEmployeePhones && !this.selectedEmployeePhones.has(press.phone)) continue
            const pressCode = press.sensorCode || press.deviceCode
            if (codes.size && pressCode && !codes.has(pressCode)) continue
            if (!codes.size && String(press.roomId) !== String(pad.roomId)) continue
            if (!lastOfflineAt || new Date(press.time) > new Date(lastOfflineAt)) {
              lastOfflineAt = press.time
            }
          }
          return {
            ...pad,
            key: [pad.roomId, pad.sensorCode, pad.deviceCode].join('|'),
            lastEnterAt,
            lastOfflineAt,
          }
        })
    },
  },
  watch: {
    siteId: {
      immediate: true,
      handler(siteId) {
        this.resetView()
        if (siteId) this.fetchData()
      },
    },
  },
  mounted() {
    this.pollTimer = setInterval(() => {
      if (document.visibilityState === 'hidden') return
      this.fetchData({ silent: true })
    }, POLL_INTERVAL_MS)
    this.nowTimer = setInterval(() => {
      this.nowTick = Date.now()
    }, NOW_TICK_MS)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    if (this.nowTimer) clearInterval(this.nowTimer)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    resetView() {
      this.requestId += 1
      this.employeeId = ''
      this.phoneNumber = ''
      this.roomId = ''
      this.events = []
      this.sessions = []
      this.offlineRelays = []
      this.pads = []
      this.companyGmt = null
      this.lastUpdated = null
      this.errorMessage = null
    },
    onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        this.fetchData({ silent: true })
      }
    },
    sessionPerson(session) {
      return session.employeeName || session.phoneNumber || '—'
    },
    sessionTime(session) {
      const value = session.isOpen ? session.enterAt : session.exitAt
      return value ? new Date(value).getTime() : 0
    },
    isRecent(timeMs) {
      if (!timeMs) return false
      const age = this.nowTick - timeMs
      return age >= 0 && age <= RECENT_MS
    },
    inTimeWindow(value) {
      if (this.timeWindow === 'month') return true
      if (!value) return false
      return new Date(value).getTime() >= this.windowStartMs
    },
    autoExitRemainingSec(session) {
      if (!session || !session.enterAt) return 0
      const elapsed = Math.round((this.nowTick - new Date(session.enterAt).getTime()) / 1000)
      return AUTO_EXIT_TOTAL_SEC - elapsed
    },
    liveDuration(from) {
      if (!from) return '—'
      const sec = Math.max(0, Math.round((this.nowTick - new Date(from).getTime()) / 1000))
      return this.formatDuration(sec)
    },
    formatPlatform(platform) {
      const value = String(platform || '').toLowerCase()
      if (value === 'ios') return 'iOS'
      if (value === 'android') return 'Android'
      return platform || ''
    },
    deviceLabel(row) {
      const os = this.formatPlatform(row && row.platform)
      const version = row && row.appVersion
      return [os, version].filter(Boolean).join(' ')
    },
    rssiClass(rssi) {
      return Number(rssi) >= ENTER_RSSI_MIN ? 'ppt__rssi--ok' : 'ppt__rssi--weak'
    },
    servikFor(btn) {
      return SERVIK_LOOKUP[Number(btn)] || null
    },
    toggleSection(section) {
      this.openSections = {
        ...this.openSections,
        [section]: !this.openSections[section],
      }
    },
    matchesFilters(row) {
      if (this.employeeId && String(row.employeeId) !== String(this.employeeId)) return false
      if (this.roomId && String(row.roomId) !== String(this.roomId)) return false
      const phone = row.phoneNumber || row.phone
      if (this.phoneNumber && phone !== this.phoneNumber) return false
      return true
    },
    async fetchData(options = {}) {
      if (!this.siteId) return
      const { timeFrom, timeTo } = lastMonthRange()

      const requestId = ++this.requestId
      if (!options.silent) this.loading = true
      this.errorMessage = null
      try {
        const data = await getPadPresenceTesting(this.siteId, {
          timeFrom,
          timeTo,
        }, { silent: !!options.silent })
        if (requestId !== this.requestId) return
        this.events = (data && data.events) || []
        this.sessions = (data && data.sessions) || []
        this.offlineRelays = (data && data.offlineRelays) || []
        this.pads = (data && data.pads) || []
        this.companyGmt = data && data.companyGmt != null ? data.companyGmt : null
        this.lastUpdated = new Date()
      } catch (error) {
        if (requestId !== this.requestId) return
        this.errorMessage = error && error.message
          ? error.message
          : this.$t('pad_presence_load_error')
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    eventBadge(event) {
      if (event.eventType === 'enter') return this.$t('pad_presence_enter')
      if (event.exitKind === 'auto') return this.$t('pad_presence_exit_auto')
      if (event.exitKind === 'stale') return this.$t('pad_presence_exit_stale')
      return this.$t('pad_presence_exit_phone')
    },
    exitTypeLabel(session) {
      if (session.isOpen) return this.$t('pad_presence_open')
      if (session.exitType === 'auto') return this.$t('pad_presence_exit_auto')
      if (session.exitType === 'stale') return this.$t('pad_presence_exit_stale')
      return this.$t('pad_presence_exit_phone')
    },
    formatTime(value) {
      if (!value) return '—'
      const datePart = utilService.companyLocalDateStr(value, this.companyGmt)
      const timePart = utilService.formatTimeInCompanyTz(value, this.companyGmt, null)
      if (datePart && timePart) return `${datePart} ${timePart}`
      return '—'
    },
    formatDuration(seconds) {
      if (seconds == null) return '—'
      const total = Number(seconds)
      if (Number.isNaN(total)) return '—'
      const minutes = Math.floor(total / 60)
      const remain = total % 60
      if (minutes < 60) return `${minutes}m ${remain}s`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ${minutes % 60}m`
    },
    offlineKey(press) {
      return [
        press.deviceCode,
        press.btn,
        press.count,
        press.time,
        press.phone,
        press.faulty ? 'f' : 'r',
      ].join('|')
    },
  },
}
</script>

<style scoped>
.ppt {
  padding: 20px 24px 40px;
  max-width: 1200px;
}

.ppt__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.ppt__title {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
}

.ppt__subtitle {
  margin: 6px 0 0;
  color: #6b7280;
}

.ppt__header-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-size: 0.9rem;
}

.ppt__updated {
  color: #6b7280;
}

.ppt__filters {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.ppt__windows {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ppt__window {
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
}

.ppt__window--on {
  background: #111827;
  border-color: #111827;
  color: #fff;
}

.ppt__warn {
  color: #9a3412;
  font-weight: 600;
}

.ppt__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
  color: #4b5563;
}

.ppt__field select {
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
}

.ppt__card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.ppt__toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 1.05rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.ppt__toggle + * {
  margin-top: 12px;
}

.ppt__chevron {
  color: #6b7280;
  transition: transform 0.15s ease;
}

.ppt__chevron--open {
  transform: rotate(180deg);
}

.ppt__timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ppt__row {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid #f3f4f6;
}

.ppt__row-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ppt__servik {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #374151;
}

.ppt__servik-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ppt__servik-icon img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.ppt__servik em {
  font-style: normal;
  color: #6b7280;
  font-weight: 700;
}

.ppt__now {
  background: #ecfdf5;
  border-color: #86efac;
}

.ppt__now-title {
  margin: 0 0 10px;
  font-size: 1.05rem;
  font-weight: 700;
}

.ppt__now-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.ppt__now-item + .ppt__now-item {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #bbf7d0;
}

.ppt__now-main {
  margin-bottom: 4px;
}

.ppt__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  color: #6b7280;
  font-size: 0.8rem;
}

.ppt__rssi {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
}

.ppt__rssi--ok {
  background: #dcfce7;
  color: #166534;
}

.ppt__rssi--weak {
  background: #ffedd5;
  color: #9a3412;
}

.ppt__row--fresh {
  background: #f5f3ff;
  margin: 0 -8px;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 8px;
}

.ppt__row-time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.ppt__row-main span,
.ppt__row time {
  color: #6b7280;
  font-size: 0.85rem;
}

.ppt__row-main .ppt__rssi,
.ppt__row-main .ppt__servik {
  color: inherit;
}

.ppt__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  background: #e5e7eb;
  color: #374151;
}

.ppt__badge--enter { background: #dcfce7; color: #166534; }
.ppt__badge--phone { background: #dbeafe; color: #1e40af; }
.ppt__badge--auto,
.ppt__badge--stale { background: #ffedd5; color: #9a3412; }
.ppt__badge--offline { background: #ede9fe; color: #5b21b6; }
.ppt__badge--faulty { background: #fee2e2; color: #991b1b; }
.ppt__badge--open { background: #fef3c7; color: #92400e; }
.ppt__badge--fresh { background: #ede9fe; color: #5b21b6; min-width: 0; }

.ppt__table-wrap {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

th,
td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
}

th {
  color: #6b7280;
  font-weight: 600;
}

.ppt__faulty {
  background: #fef2f2;
}

.ppt__fresh {
  background: #f5f3ff;
}

.ppt__empty,
.ppt__error {
  padding: 40px 16px;
  text-align: center;
}

.ppt__empty-title {
  margin: 0 0 8px;
  font-weight: 600;
}

.ppt__empty-desc {
  margin: 0;
  color: #9ca3af;
}

.ppt__error {
  color: #b91c1c;
}

@media (max-width: 767px) {
  .ppt {
    padding: 12px;
  }

  .ppt__header {
    flex-direction: column;
  }

  .ppt__header-meta {
    align-items: flex-start;
  }

  .ppt__row {
    grid-template-columns: 1fr;
  }
}
</style>
